import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
    Table, TableBody, TableCell, TableHead,
    TableHeader, TableRow,
} from "@/components/ui/table"
import {
    Pagination, PaginationContent, PaginationItem,
    PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination"
import { ChevronDown, FileText, Filter, Loader2, X } from "lucide-react"
import { getReportsByInstrumentPaginated } from "@/Wist-blockchain/collections/serviceReport/getAllServiceReport"
import { useNavigate } from "@tanstack/react-router"

const INITIAL_LIMIT = 10
const RECORDS_PER_PAGE = 5

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—"
    const datePart = dateStr.split("T")[0]
    return new Date(`${datePart}T12:00:00`).toLocaleDateString("es-EC", {
        day: "2-digit", month: "short", year: "numeric",
    })
}

const SERVICE_TYPE_LABELS: Record<string, string> = {
    instalacion:               "Instalación",
    preventivo:                "Preventivo",
    correctivo:                "Correctivo",
    "calificacion-validacion": "Calificación / Validación",
    capacitacion:              "Capacitación",
    otro:                      "Otro",
}

const SERVICE_REASON_LABELS: Record<string, string> = {
    instalacion:                "Instalación",
    "mantenimiento-programado": "Mantenimiento programado",
    "falla-reportada":          "Falla reportada",
    "revision-anomalia":        "Revisión por anomalía",
    otro:                       "Otro",
}

const FINAL_STATUS_LABELS: Record<string, { label: string; className: string }> = {
    operativo:               { label: "Operativo",            className: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" },
    operativo_observaciones: { label: "Requiere seguimiento", className: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100" },
    fuera_servicio:          { label: "Fuera de servicio",    className: "bg-red-100 text-red-600 border-red-200 hover:bg-red-100" },
}

interface Props {
    instrumentId: string
}

export const CertificationHistoryCard = ({ instrumentId }: Props) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [showAll, setShowAll] = useState(false)
    const [showFilters, setShowFilters] = useState(false)
    const [filterType, setFilterType] = useState("all")
    const [filterStatus, setFilterStatus] = useState("all")
    const navigate = useNavigate()

    useEffect(() => { setCurrentPage(1) }, [filterType, filterStatus, showAll])

    const { data, isLoading, isError } = useQuery({
        queryKey: ["service-reports", instrumentId, showAll],
        queryFn: () => getReportsByInstrumentPaginated(instrumentId, showAll ? 5000 : INITIAL_LIMIT),
    })

    const allRecords = data?.documents ?? []
    const total = data?.total ?? 0
    const hasMore = !showAll && total > INITIAL_LIMIT

    const filteredRecords = allRecords
        .filter(r => {
            if (filterType !== "all") {
                const inOld = r.serviceType === filterType
                const inNew = r.serviceTypes?.includes(filterType) ?? false
                if (!inOld && !inNew) return false
            }
            if (filterStatus !== "all" && r.finalStatus !== filterStatus) return false
            return true
        })
        .sort((a, b) => (b.$createdAt ?? "").localeCompare(a.$createdAt ?? ""))

    const activeFilters = (filterType !== "all" ? 1 : 0) + (filterStatus !== "all" ? 1 : 0)
    const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE)
    const visibleRecords = showAll
        ? filteredRecords
        : filteredRecords.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE)

    const clearFilters = () => { setFilterType("all"); setFilterStatus("all") }

    return (
        <Card className="border-gray-200">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold text-gray-800 dark:text-gray-400">
                        Historial de Calificaciones y Certificados
                    </CardTitle>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowFilters(v => !v)}
                        className={`gap-1.5 h-8 text-xs cursor-pointer ${showFilters ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30" : "text-gray-500"}`}
                    >
                        <Filter className="h-3.5 w-3.5" />
                        Filtrar
                        {activeFilters > 0 && (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white font-bold">
                                {activeFilters}
                            </span>
                        )}
                    </Button>
                </div>

                {showFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className="h-8 w-48 text-xs cursor-pointer">
                                <SelectValue placeholder="Tipo de servicio" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los tipos</SelectItem>
                                {Object.entries(SERVICE_TYPE_LABELS).map(([val, label]) => (
                                    <SelectItem key={val} value={val}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="h-8 w-48 text-xs cursor-pointer">
                                <SelectValue placeholder="Estado final" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                {Object.entries(FINAL_STATUS_LABELS).map(([val, cfg]) => (
                                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {activeFilters > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                className="h-8 gap-1 text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                            >
                                <X className="h-3 w-3" />
                                Limpiar
                            </Button>
                        )}
                    </div>
                )}
            </CardHeader>

            <CardContent className="px-0 pb-0">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Cargando reportes...</span>
                    </div>
                ) : isError ? (
                    <p className="py-8 text-center text-sm text-red-500">Error al cargar los reportes.</p>
                ) : allRecords.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">No hay reportes registrados para este equipo.</p>
                ) : filteredRecords.length === 0 ? (
                    <div className="py-8 text-center space-y-2">
                        <p className="text-sm text-gray-400">Sin resultados con los filtros actuales.</p>
                        <Button variant="link" size="sm" onClick={clearFilters} className="text-xs text-blue-600 cursor-pointer">
                            Limpiar filtros
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="text-xs uppercase tracking-wide text-gray-400">
                                        <TableHead className="font-semibold">Tipo / ID</TableHead>
                                        <TableHead className="font-semibold">Fecha</TableHead>
                                        <TableHead className="font-semibold">Estado</TableHead>
                                        <TableHead className="font-semibold hidden sm:table-cell">Motivo</TableHead>
                                        <TableHead className="font-semibold">Anexos</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleRecords.map((record) => {
                                        const status = FINAL_STATUS_LABELS[record.finalStatus ?? ""] ?? {
                                            label: record.finalStatus ?? "—",
                                            className: "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100",
                                        }
                                        return (
                                            <TableRow
                                                key={record.$id}
                                                className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30 cursor-pointer"
                                                onClick={() => navigate({ to: '/instruments/report/$id', params: { id: record.$id } })}
                                            >
                                                <TableCell>
                                                    <p className="font-medium text-sm">
                                                        {record.serviceTypes?.length
                                                            ? record.serviceTypes.map((t: string) => SERVICE_TYPE_LABELS[t] ?? t).join(", ")
                                                            : (SERVICE_TYPE_LABELS[record.serviceType ?? ""] ?? record.serviceType ?? "—")}
                                                    </p>
                                                    <p className="text-xs text-gray-400 font-mono">{record.idReport}</p>
                                                    {record.parentReportId && (
                                                        <Badge variant="outline" className="text-[10px] mt-0.5 border-amber-300 text-amber-600">
                                                            REV{record.revisionNumber}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                    {formatDate(record.serviceDate)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`border text-xs font-semibold ${status.className}`}>
                                                        {status.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden sm:table-cell text-sm text-gray-600 dark:text-gray-400">
                                                    {SERVICE_REASON_LABELS[record.serviceReason ?? ""] ?? record.serviceReason ?? "—"}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <FileText className="h-3 w-3" />
                                                        {record.idAttachments?.length ?? 0}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Footer */}
                        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-800 px-4 py-3 sm:flex-row">
                            <p className="text-xs text-gray-500">
                                Mostrando{" "}
                                <span className="font-semibold text-gray-700 dark:text-gray-300">
                                    {showAll ? filteredRecords.length : Math.min(allRecords.length, filteredRecords.length)}
                                </span>{" "}
                                de{" "}
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{total}</span>{" "}
                                registros
                            </p>

                            <div className="flex items-center gap-3">
                                {!showAll && totalPages > 1 && (
                                    <Pagination>
                                        <PaginationContent>
                                            <PaginationItem>
                                                <PaginationPrevious
                                                    href="#"
                                                    onClick={(e) => { e.preventDefault(); setCurrentPage(p => Math.max(1, p - 1)) }}
                                                    className={currentPage === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"}
                                                />
                                            </PaginationItem>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                <PaginationItem key={page}>
                                                    <PaginationLink
                                                        href="#"
                                                        isActive={page === currentPage}
                                                        onClick={(e) => { e.preventDefault(); setCurrentPage(page) }}
                                                        className="cursor-pointer"
                                                    >
                                                        {page}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationItem>
                                                <PaginationNext
                                                    href="#"
                                                    onClick={(e) => { e.preventDefault(); setCurrentPage(p => Math.min(totalPages, p + 1)) }}
                                                    className={currentPage === totalPages ? "pointer-events-none opacity-40" : "cursor-pointer"}
                                                />
                                            </PaginationItem>
                                        </PaginationContent>
                                    </Pagination>
                                )}

                                {hasMore && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setShowAll(true)}
                                        className="text-blue-600 hover:text-blue-700 text-xs px-0 gap-1 cursor-pointer"
                                    >
                                        <ChevronDown className="h-3.5 w-3.5" />
                                        Ver {total - INITIAL_LIMIT} registros más
                                    </Button>
                                )}

                                {showAll && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => { setShowAll(false); setCurrentPage(1) }}
                                        className="text-gray-400 hover:text-gray-600 text-xs px-0 cursor-pointer"
                                    >
                                        Mostrar menos
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    )
}
